import { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom'
import routers from '@/router/index'
import ErrorBoundary from '@/components/ErrorBoundary'
import PrivateRoute from '@/components/PrivateRoute/PrivateRoute'
import { progress } from '@/components/progressBar/progressBar'
import { refreshUserInfo } from '@/utils/userRole'

const NotFound = lazy(() => import('@/pages/404'))

// Minimal loading fallback (progress bar handles visual feedback)
const LoadingFallback = () => {
	useEffect(() => {
		progress.start()
		return () => {
			progress.done()
		}
	}, [])
	return null
}

// Route change listener component
const RouteChangeListener = () => {
	const location = useLocation()
	const isFirstRender = useRef(true)

	useEffect(() => {
		// Skip the first render (initial page load)
		if (isFirstRender.current) {
			isFirstRender.current = false
			return
		}
		// Start progress on route change
		progress.start()
		// Complete after a short delay (page rendered)
		const timer = setTimeout(() => {
			progress.done()
		}, 100)
		return () => clearTimeout(timer)
	}, [location.pathname])

	return null
}

// Admin route guard - redirects non-admin users
const AdminRoute = ({ children }: { children: React.ReactNode }) => {
	const navigate = useNavigate()
	const [checking, setChecking] = useState(true)
	const [authorized, setAuthorized] = useState(false)

	useEffect(() => {
		refreshUserInfo().then(({ isAdmin }) => {
			if (!isAdmin) {
				navigate('/console', { replace: true })
			} else {
				setAuthorized(true)
			}
			setChecking(false)
		})
	}, [])

	if (checking) return null
	if (!authorized) return null
	return <>{children}</>
}

const Router = () => {
	useEffect(() => {
		const revalidateSession = () => {
			if (localStorage.getItem('userInfo')) {
				void refreshUserInfo()
			}
		}

		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible') {
				revalidateSession()
			}
		}

		window.addEventListener('focus', revalidateSession)
		document.addEventListener('visibilitychange', handleVisibilityChange)

		return () => {
			window.removeEventListener('focus', revalidateSession)
			document.removeEventListener('visibilitychange', handleVisibilityChange)
		}
	}, [])

	const myRoutes = routers
		.filter(item => item.path !== '/404') // Exclude /404 from regular routes
		.map((item) => {
			if (item.redirectTo) {
				return (
					<Route
						key={item.path}
						path={item.path}
						element={<Navigate to={item.redirectTo} replace />}
					/>
				)
			}
			const Component = item.component
			if (!Component) {
				return null
			}
			return (
				<Route
					key={item.path}
					path={item.path}
					element={
						<ErrorBoundary>
							{item.requireAuth ? (
								<PrivateRoute>
									{item.requireAdmin ? (
										<AdminRoute>
											<Component />
										</AdminRoute>
									) : (
										<Component />
									)}
								</PrivateRoute>
							) : (
								<Component />
							)}
						</ErrorBoundary>
					}
				/>
			)
		})
		.filter(Boolean) // Remove any null routes
	
	return (
		<ErrorBoundary>
			<RouteChangeListener />
			<Suspense fallback={<LoadingFallback />}>
				<Routes>
					{myRoutes}
								<Route path="/console" element={<Navigate to="/console/compute/pods" replace />} />
					<Route 
						path="*" 
						element={
							<ErrorBoundary>
								<NotFound />
							</ErrorBoundary>
						} 
					/>
				</Routes>
			</Suspense>
		</ErrorBoundary>
	)
}

export default Router
